import { useState, useEffect } from "react";
import { Stack, Box } from "@mui/material";

import MapDisplay from "../../../components/atoms/MapDisplay";
import ButtonComponent from "../../../components/atoms/ButtonComponent";
import InputComponent from "../../../components/atoms/InputComponent";
import BasesFormModal from "../../../components/molecules/BasesFormModal";
import useWexClient from "../../../hooks/Clients/wexClient,";

function HeatMap({
  serviceId,
  selectedAgent = "",
  users = [],
  bases = [],
  onBasesChange = () => {},
  permissions = {},
}) {
  const [receive] = useState("HeatMap");
  const [heatPoints, setHeatPoints] = useState([]);
  // busqueda de base
  const [searchBaseQuery, setSearchBaseQuery] = useState("");
  const [baseLocation, setBaseLocation] = useState(null);
  const [baseLabel, setBaseLabel] = useState("");
  const [baseModalOpen, setBaseModalOpen] = useState(false);
  const [baseModalForCreate, setBaseModalForCreate] = useState(true);
  const [selectedBaseData, setSelectedBaseData] = useState(null);
  const { geocodingSearch } = useWexClient();

  useEffect(() => {
    let filteredUsers = users;

    // Filtrar por agente
    if (selectedAgent && selectedAgent !== "00001") {
      filteredUsers = filteredUsers.filter(
        (u) => String(u.id) === String(selectedAgent),
      );
    }

    // Transformar coordenadas
    const points = filteredUsers
      .filter((u) => u.location || u.ubicationClient)
      .map((u) => {
        const coords = u.location || u.ubicationClient;

        // Validar formato
        if (!coords.includes(",")) return null;

        const [lat, lng] = coords.split(",").map(Number);

        // Validar números
        if (isNaN(lat) || isNaN(lng)) return null;

        return {
          lat,
          lng,
          folio: u.folio,
          minutes: u.minutes,
          attendedOnTime: u.attendedOnTime,
          createdAt: u.createdAt,
          closeAt: u.closeAt,
        };
      })
      .filter(Boolean);

    setHeatPoints(points);
  }, [users, selectedAgent]);

  const searchBase = async (query = "") => {
    const trimmedQuery = String(query ?? "").trim();
    if (!trimmedQuery) return;

    try {
      const response = await geocodingSearch({ query: trimmedQuery });
      const payload = response?.data ?? response?.result ?? response;
      const bestResult = Array.isArray(payload) ? payload[0] : payload;

      const extractLocation = (value) => {
        if (Array.isArray(value)) {
          const firstItem = value[0];
          if (
            firstItem &&
            Number.isFinite(Number(firstItem.lat)) &&
            Number.isFinite(Number(firstItem.lng))
          ) {
            return {
              lat: Number(firstItem.lat),
              lng: Number(firstItem.lng),
              label: firstItem.label || firstItem.name || trimmedQuery,
            };
          }
          return null;
        }

        if (!value || typeof value !== "object") return null;

        if (
          Number.isFinite(Number(value.lat)) &&
          Number.isFinite(Number(value.lng))
        ) {
          return {
            lat: Number(value.lat),
            lng: Number(value.lng),
            label: value.label || value.name || trimmedQuery,
          };
        }

        const direct =
          value.location ??
          value.coordinates ??
          value.coords ??
          value.position ??
          value.geometry?.location;

        if (direct && typeof direct === "object") {
          return {
            ...direct,
            label: value.label || value.name || trimmedQuery,
          };
        }

        return null;
      };

      const location = extractLocation(bestResult ?? payload);

      if (location) {
        const lat = Number(
          location.lat ?? location.latitude ?? location.y ?? location[0],
        );
        const lng = Number(
          location.lng ?? location.longitude ?? location.lon ?? location[1],
        );

        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setBaseLocation({ lat, lng });
          setBaseLabel(location.label || trimmedQuery || "Base buscada");
          setSearchBaseQuery("");
          return;
        }
      }

      if (response?.success === false) {
        console.error(response?.message || "No se encontró la ubicación.");
      }
    } catch (error) {
      console.error("Error al buscar base con geocodificación:", error);
    }
  };

  const handleSearchBase = async () => {
    try {
      await searchBase(searchBaseQuery);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCtrlClick = (location) => {
    setBaseLocation(location);
    setBaseLabel(
      `Base: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
    );
    setSearchBaseQuery("");
  };

  const handleBaseMarkerClick = (base = null) => {
    if (base) {
      setSelectedBaseData({
        ...base,
        _id: base._id || base.id,
        status: base.status ?? true,
        coordinates: `${base.lat},${base.lng}`,
      });
      setBaseModalForCreate(false);
    } else if (baseLocation) {
      setSelectedBaseData({
        description: baseLabel || "Base buscada",
        isConfirmed: false,
        coordinates: `${baseLocation.lat},${baseLocation.lng}`,
      });
      setBaseModalForCreate(true);
    } else {
      setSelectedBaseData(null);
      setBaseModalForCreate(true);
    }

    setBaseModalOpen(true);
  };

  const handleCloseBaseModal = () => {
    setBaseModalOpen(false);
    setSelectedBaseData(null);
    setBaseModalForCreate(true);
    setBaseLocation(null);
    setBaseLabel("");
  };

  return (
    <Stack>
      <Box
        sx={{
          position: "absolute",
          /* Bajas la posición 'top' en desktop (md: 70) para que no sea tapada por el navbar */
          top: { xs: 9, sm: 18, md: 25 },
          left: 0,
          right: 0,
          zIndex: 20,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          pointerEvents: "none",
        }}
      >
        {/* Buscador de Coordenadas / Incidentes */}
        <Box
          sx={{
            zIndex: 1000,
            pointerEvents: "auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 1.5 },
            backgroundColor: "#FFFFFF",
            p: { xs: 1.25, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid #EAE8F5",
            boxShadow: "0px 10px 30px rgba(110, 107, 180, 0.12)",
            width: { xs: "100%", sm: "85%", md: "760px" },
            maxWidth: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <InputComponent
              label="Introduzca las coordenadas"
              value={searchBaseQuery || ""}
              onChange={(val) => setSearchBaseQuery(val)}
              textColor="#0c4a91"
              backgroundColor="#F8F7FC"
              width="100%"
              fontSize="14px"
            />
          </Box>

          <Box
            sx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "160px" },
              pt: { xs: 0.5, sm: 2.8 },
            }}
          >
            <ButtonComponent
              label="Buscar Base"
              onClick={handleSearchBase}
              backgroundColor="#0c4a91"
              color="white"
              width="100%"
              style={{
                borderRadius: "12px",
                boxShadow: "0px 4px 12px #0c4a91",
                fontWeight: 600,
                height: "38px",
              }}
            />
          </Box>
        </Box>
      </Box>
      <MapDisplay
        receive={receive}
        users={heatPoints}
        AdjusterBases={bases}
        baseLocation={baseLocation}
        baseLabel={baseLabel}
        onBaseMarkerClick={handleBaseMarkerClick}
        onCtrlClick={handleCtrlClick}
        zoom={6}
        permissions={permissions}
      />
      <BasesFormModal
        open={baseModalOpen}
        onClose={handleCloseBaseModal}
        serviceId={serviceId}
        forCreate={baseModalForCreate}
        base={selectedBaseData}
        onSaved={onBasesChange}
      />
    </Stack>
  );
}

export default HeatMap;
